import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Grid, 
  CircularProgress,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';

const CoordinadorSection = ({ 
  value, 
  onChange, 
  onSearchPerson,
  onContactChange,
  otherRoles = [],
  error,
  cuilInputRef,
  mailInputRef
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleCuilSearch = async () => {
    if (!value?.cuil || value.cuil.length !== 11) {
      setSearchError('Son necesarios 11 caracteres numéricos');
      onChange({
        ...value,
        nombre: '',
        apellido: '',
        mail: '',
        celular: ''
      });
      return;
    }
    setSearchError('');
    setIsSearching(true);
    
    try {
      let response;
      if (onSearchPerson) {
        response = await onSearchPerson(value.cuil);
      }

      if (response && response.success) {
        onChange({
          ...value,
          nombre: response.data.nombre || '',
          apellido: response.data.apellido || '',
          mail: response.data.mail || response.data.correo || '',
          celular: response.data.celular || ''
        });
      } else {
        setSearchError(response?.message || 'Persona no encontrada');
        onChange({
          ...value,
          nombre: '',
          apellido: '',
          mail: '',
          celular: ''
        });
      }
    } catch (err) {
      setSearchError('Error al buscar. Intente nuevamente.');
      onChange({
        ...value,
        nombre: '',
        apellido: '',
        mail: '',
        celular: ''
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleCuilChange = (e) => {
    const rawValue = e.target.value;
    const numericCuil = rawValue.replace(/\D/g, '').slice(0, 11);
    setSearchError('');
    // Al cambiar el CUIL, limpiar los campos autocompletados
    onChange({
      ...value,
      cuil: numericCuil,
      nombre: '',
      apellido: '',
      mail: '',
      celular: ''
    });
  };

  const handleContactFieldChange = (field) => (e) => {
    let newValue = e.target.value;
    if (field === 'celular') {
      newValue = newValue.replace(/\D/g, '').slice(0, 10);
    }
    // 1. Actualizar estado local
    onChange({
      ...value,
      [field]: newValue
    });
    // 2. Sincronizar en todo el formulario si hay CUIL
    if (value?.cuil && onContactChange) {
      onContactChange(value.cuil, field, newValue);
    }
  };

  const isSynced = otherRoles && otherRoles.length > 0;
  const isCuilIncomplete = Boolean(value?.cuil && value.cuil.length > 0 && value.cuil.length < 11);
  const cuilHelperText = error?.cuil || searchError || (isCuilIncomplete ? 'Son necesarios 11 caracteres numéricos' : '');
  const hasCuilError = Boolean(error?.cuil || searchError || isCuilIncomplete);

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" color="primary">
            Coordinador/a (Gestión de Accesos)
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Ingrese el CUIL para buscar los datos del coordinador. El correo y celular modificados se sincronizan automáticamente en todo el formulario.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={<span>CUIL {!value?.cuil?.trim() && <span style={{ color: '#E2464C' }}>*</span>}</span>}
              variant="outlined"
              inputRef={cuilInputRef}
              value={value?.cuil || ''}
              onChange={handleCuilChange}
              error={hasCuilError}
              helperText={cuilHelperText}
              inputProps={{
                maxLength: 11,
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCuilSearch} edge="end" disabled={isSearching || isCuilIncomplete}>
                      {isSearching ? <CircularProgress size={24} /> : <SearchIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {isSynced && (
              <Box sx={{ mt: 1 }}>
                <Tooltip title={`Esta persona también figura como: ${otherRoles.join(', ')}. Los datos de contacto están sincronizados.`}>
                  <Chip
                    icon={<SyncIcon />}
                    label={`Sincronizado con: ${otherRoles.join(', ')}`}
                    color="info"
                    variant="outlined"
                    size="small"
                    sx={{
                      fontWeight: 'medium',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': {
                        display: 'block',
                        whiteSpace: 'normal',
                        py: 0.5,
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nombre"
              variant="outlined"
              value={value?.nombre || ''}
              disabled={true}
              error={!!error?.nombre}
              helperText={error?.nombre || 'Obtenido automáticamente por CUIL'}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Apellido"
              variant="outlined"
              value={value?.apellido || ''}
              disabled={true}
              error={!!error?.apellido}
              helperText={error?.apellido || 'Obtenido automáticamente por CUIL'}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Correo Electrónico"
              type="email"
              variant="outlined"
              inputRef={mailInputRef}
              value={value?.mail || value?.correo || ''}
              onChange={handleContactFieldChange('mail')}
              disabled={true}
              error={!!error?.mail}
              helperText={error?.mail || 'Obtenido automáticamente por CUIL'}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Celular (Opcional)"
              variant="outlined"
              value={value?.celular || ''}
              onChange={handleContactFieldChange('celular')}
              disabled={isSearching}
              error={!!error?.celular}
              helperText={error?.celular || 'Máximo 10 dígitos (ej: 3511234567)'}
              inputProps={{
                maxLength: 10,
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CoordinadorSection;
